import { MS_PER_SECOND } from '~constants/session';
import { tponeUrl } from '~constants/tpone';

import { get as getLocalStorageItem, set as setLocalStorageItem } from '~libs/localstorage';

/**
 * How this browser last signed in: 'sso' through TP1 with the organisation's single sign-on, 'tp1' through TP1
 * with a password, 'password' on the TAV sign-in page. A session that ends sends the user back the same way,
 * whatever the realm.
 */
export type SignInRoute = 'tp1' | 'sso' | 'password';

// localStorage, so the marker survives removeAuthenticated and a closed tab.
export const SIGN_IN_ROUTE_KEY = 'tav-sign-in-route';
// sessionStorage: when this tab last left for TP1. Bounds redirect loops (failed handoff, a cookie that won't
// stick, no proof support).
export const TP1_REDIRECT_AT_KEY = 'tav-tp1-redirect-at';
export const TP1_REDIRECT_GUARD_MS = 60 * MS_PER_SECOND;
export const TP1_SIGN_IN_PATH = '/tav-go.asp?to=home';
// sessionStorage: the page this tab was on when it left for TP1, so the trip back through TP1 (which always lands on
// home) can return to it. No time limit: a session often ends with nobody at the screen. Used once, in this tab only.
export const TP1_RETURN_TO_KEY = 'tav-tp1-return-to';
// Sign-in pages are never a page to come back to.
const NON_RETURN_PATHS = ['/login', '/logout', '/auth-session'];

/** Injectable so tests can spy on it: window.location cannot be reliably spied on in jsdom. */
export const externalNavigation = {
  assign: (url: string) => window.location.assign(url),
  /**
   * Opens a page in a new tab, leaving this one (and anything unsaved on it) where it is. Returns false when the
   * browser blocked the new tab. Opened without 'noopener' so a block can be detected (window.open then returns
   * null), and the opener link is cut straight away instead.
   */
  open: (url: string): boolean => {
    const opened = window.open(url, '_blank');
    if (!opened) return false;
    opened.opener = null;
    return true;
  },
};

export const recordSignInRoute = (route: SignInRoute) => {
  setLocalStorageItem(SIGN_IN_ROUTE_KEY, route);
};

export const getSignInRoute = (): SignInRoute => {
  const stored = getLocalStorageItem(SIGN_IN_ROUTE_KEY);
  if (stored === 'sso') return 'sso';
  if (stored === 'tp1') return 'tp1';
  return 'password';
};

/** The TP1 URL that signs the user in and hands them back to TAV, or null when TP1's address is unusable. */
export const tp1SignInUrl = (): string | null => {
  try {
    return new URL(TP1_SIGN_IN_PATH, tponeUrl).toString();
  } catch {
    return null;
  }
};

/**
 * Signed in through TP1, with a password or SSO: a session that ends sends them back through TP1, which shows its
 * own sign-in (the TP1 login page, or the organisation's SSO). Users who signed in on the TAV login page go back there.
 */
export const usesTp1SignIn = () => getSignInRoute() !== 'password' && tp1SignInUrl() !== null;

/** Signed in through TP1 with SSO: the session-limit prompt offers the organisation sign-in instead of a password. */
export const usesSsoSignIn = () => getSignInRoute() === 'sso' && tp1SignInUrl() !== null;

/**
 * Inside a frame (TAV embedded in a TP1 page) a bounce through TP1 would lose the embedded layout and the page,
 * and the TP1 page around it handles its own timeout, so frames keep the previous behaviour.
 */
export const isEmbedded = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const isReturnablePath = (path: unknown): path is string =>
  typeof path === 'string' &&
  path.startsWith('/') &&
  // '//host' and '/\host' are other sites to a browser
  !path.startsWith('//') &&
  !path.startsWith('/\\') &&
  !NON_RETURN_PATHS.some((nonReturn) => path === nonReturn || path.startsWith(`${nonReturn}?`));

const rememberReturnPath = () => {
  try {
    const path = window.location.pathname + window.location.search;
    if (isReturnablePath(path)) {
      window.sessionStorage.setItem(TP1_RETURN_TO_KEY, path);
    } else {
      window.sessionStorage.removeItem(TP1_RETURN_TO_KEY);
    }
  } catch {
    // Without storage the user comes back to home, as TP1 sends them there.
  }
};

/**
 * Sends the browser to TP1 to sign in. Returns false, leaving the page as it is, when there is no TP1 URL, inside
 * a frame, or when this tab already left for TP1 within the guard window (or storage is unavailable), so the caller can
 * fall back to the password form. Explicit clicks pass `ignoreGuard`.
 */
export const leaveForTp1SignIn = ({ ignoreGuard = false }: { ignoreGuard?: boolean } = {}): boolean => {
  const url = tp1SignInUrl();
  if (!url || isEmbedded()) return false;

  try {
    if (!ignoreGuard) {
      const last = Number(window.sessionStorage.getItem(TP1_REDIRECT_AT_KEY));
      if (last && Date.now() - last < TP1_REDIRECT_GUARD_MS) return false;
    }
    window.sessionStorage.setItem(TP1_REDIRECT_AT_KEY, String(Date.now()));
  } catch {
    // Without storage the guard cannot work, and an unguarded redirect could loop.
    if (!ignoreGuard) return false;
  }

  rememberReturnPath();
  externalNavigation.assign(url);
  return true;
};

/**
 * The page this tab left for TP1 from, or null. Clears it either way, so it is used at most once.
 */
export const takeTp1ReturnPath = (): string | null => {
  try {
    const stored = window.sessionStorage.getItem(TP1_RETURN_TO_KEY);
    window.sessionStorage.removeItem(TP1_RETURN_TO_KEY);
    return isReturnablePath(stored) ? stored : null;
  } catch {
    return null;
  }
};
